import { FC, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, message, Spin } from "antd";
import { useMenu } from "src/contexts/MenuContext";
import {
  DASHBOARD_APP_BASE,
  buildUserAccessibleDashboardsFromApi,
  getSupportedDashboardByRoute,
  SupportedDashboardRoute,
} from "src/layouts/HomePage/Config/DashboardRoutes";
import {
  DashboardApiError,
  fetchAllDashboardsAPI,
} from "src/services/AccountDashboardAPIs";
import { getApiLangFromI18n } from "src/utils/apiLang";
import { WebAnalyticsDashboard } from "src/dashboards/webAnalytics";
import { OrganicMediaDashboard } from "src/dashboards/organicMedia";
import { ShopifyDashboard } from "src/dashboards/shopify";
import { CLTVAnalysisDashboard } from "src/dashboards/cltv";
import { PaidMediaDashboard } from "src/dashboards/paidMedia";
import { SalesProductAnalyticsDashboard } from "src/dashboards/salesProductAnalytics";
import { OmnichannelAnalyticsDashboard } from "src/dashboards/omnichannelAnalytics";
import { AttributionDashboard } from "src/dashboards/attribution";
import "./DashboardRouteShell.scss";

const DASHBOARDS_BASE_PATH = DASHBOARD_APP_BASE;

const DashboardPage: FC = (): JSX.Element | null => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { setSelectedMenuItem } = useMenu();

  const [userAccessibleDashboards, setUserAccessibleDashboards] = useState<
    SupportedDashboardRoute[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardErrorMessage, setDashboardErrorMessage] = useState<string | null>(
    null
  );

  const activeDashboard = useMemo(
    () => getSupportedDashboardByRoute(location.pathname),
    [location.pathname]
  );

  useEffect(() => {
    let mounted = true;

    const loadDashboards = async () => {
      const userId = localStorage.getItem("user_id");
      const apiKey = localStorage.getItem("api_key");

      if (!userId || !apiKey) {
        navigate("/log-in");
        return;
      }

      try {
        const apiDashboards = await fetchAllDashboardsAPI(
          userId,
          apiKey,
          getApiLangFromI18n(i18n),
        );
        if (!mounted) {
          return;
        }

        setDashboardErrorMessage(null);
        setUserAccessibleDashboards(
          buildUserAccessibleDashboardsFromApi(apiDashboards),
        );
      } catch (error) {
        if (mounted) {
          if (error instanceof DashboardApiError) {
            setDashboardErrorMessage(error.message);
            message.error(error.message);
          } else {
            message.error(t("login.somethingWentWrong"));
          }
          setUserAccessibleDashboards([]);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadDashboards();

    return () => {
      mounted = false;
    };
  }, [navigate, t, i18n.language, i18n.resolvedLanguage]);

  useEffect(() => {
    if (!activeDashboard) {
      setSelectedMenuItem("");
      return;
    }

    const matchedDashboard = userAccessibleDashboards.find(
      dashboard => dashboard.dashboardId === activeDashboard.dashboardId,
    );

    setSelectedMenuItem(
      matchedDashboard?.dashboardName ?? activeDashboard.dashboardName,
    );
  }, [activeDashboard, userAccessibleDashboards, setSelectedMenuItem]);

  /** Enforce URL matches backend-assigned dashboards only (order = API order; first wins). */
  useEffect(() => {
    if (isLoading || dashboardErrorMessage) {
      return;
    }

    const first = userAccessibleDashboards[0];
    if (!first) {
      return;
    }

    const onDashboardsRoot =
      location.pathname === DASHBOARDS_BASE_PATH ||
      location.pathname === `${DASHBOARDS_BASE_PATH}/`;

    const allowed = activeDashboard
      ? userAccessibleDashboards.some(
          d => d.dashboardId === activeDashboard.dashboardId,
        )
      : false;

    if (onDashboardsRoot || !activeDashboard || !allowed) {
      navigate(first.route, { replace: true });
    }
  }, [
    activeDashboard,
    dashboardErrorMessage,
    isLoading,
    location.pathname,
    navigate,
    userAccessibleDashboards,
  ]);

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 240,
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (dashboardErrorMessage) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" message={dashboardErrorMessage} showIcon />
      </div>
    );
  }

  if (userAccessibleDashboards.length === 0) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="info" message={t("login.noDashboards")} showIcon />
      </div>
    );
  }

  if (!activeDashboard) {
    return null;
  }

  const isDashboardAllowed = userAccessibleDashboards.some(
    dashboard => dashboard.dashboardId === activeDashboard.dashboardId,
  );

  if (!isDashboardAllowed) {
    return null;
  }

  if (activeDashboard.dashboardId === 280) {
    return (
      <div
        className="dashboard-route-shell dashboard-route-shell--shopify"
        style={{ position: "relative", zIndex: 0 }}
      >
        <ShopifyDashboard />
      </div>
    );
  }

  if (activeDashboard.dashboardId === 293) {
    return (
      <div
        className="dashboard-route-shell dashboard-route-shell--web-analytics"
        style={{ position: "relative", zIndex: 0 }}
      >
        <WebAnalyticsDashboard />
      </div>
    );
  }

  if (activeDashboard.dashboardId === 301) {
    return (
      <div style={{ position: "relative", zIndex: 0, overflow: "hidden" }}>
        <OrganicMediaDashboard />
      </div>
    );
  }

  if (activeDashboard.dashboardId === 296) {
    return (
      <div
        className="dashboard-route-shell dashboard-route-shell--cltv"
        style={{ position: "relative", zIndex: 0 }}
      >
        <CLTVAnalysisDashboard />
      </div>
    );
  }

  if (activeDashboard.dashboardId === 302) {
    return (
      <div
        className="dashboard-route-shell dashboard-route-shell--paid-media"
        style={{ position: "relative", zIndex: 0 }}
      >
        <PaidMediaDashboard />
      </div>
    );
  }

  if (activeDashboard.dashboardId === 303) {
    return (
      <div
        className="dashboard-route-shell dashboard-route-shell--attribution"
        style={{ position: "relative", zIndex: 0 }}
      >
        <AttributionDashboard />
      </div>
    );
  }

  if (activeDashboard.dashboardId === 304) {
    return (
      <div
        className="dashboard-route-shell dashboard-route-shell--sales-product"
        style={{ position: "relative", zIndex: 0 }}
      >
        <SalesProductAnalyticsDashboard />
      </div>
    );
  }

  if (activeDashboard.dashboardId === 3) {
    return (
      <div
        className="dashboard-route-shell dashboard-route-shell--omnichannel"
        style={{ position: "relative", zIndex: 0 }}
      >
        <OmnichannelAnalyticsDashboard />
      </div>
    );
  }

  return null;
};

export default DashboardPage;
